import { useEffect } from 'react'

/**
 * Avisa por consola cuando la página se va de costado, y dice quién la empuja.
 *
 * Existe porque este error es de los que no se ven donde se trabaja. Una grilla
 * que se niega a encogerse desborda en un teléfono de 360 px y se ve perfecta en
 * el monitor donde se la escribió; el que la encuentra es el jugador, en el
 * salón, arrastrando la pantalla de costado con el pulgar.
 *
 * Detectarlo es una resta —cuánto más ancho es el documento que la ventana— y lo
 * caro es solo encontrar al culpable, que se hace únicamente cuando ya hay
 * desborde. Corre nada más que en desarrollo: `import.meta.env.DEV` queda en
 * `false` al compilar y el empaquetador se lleva todo el cuerpo, así que en
 * producción sobrevive un gancho vacío de cuarenta bytes.
 */
export function useAvisoDeDesborde(): void {
  useEffect(() => {
    if (!import.meta.env.DEV) return

    function revisar(): void {
      const raiz = document.documentElement
      // Un píxel de tolerancia: el redondeo de los navegadores da sobras de
      // fracciones que no son un error de maquetación.
      const sobra = raiz.scrollWidth - raiz.clientWidth
      if (sobra <= 1) return

      const borde = raiz.clientWidth + 1
      const culpables = [...document.querySelectorAll('body *')].filter((elemento) => {
        const caja = elemento.getBoundingClientRect()
        return caja.width > 0 && (caja.right > borde || caja.left < -1)
      })

      /*
       * El más profundo de la lista es el que conviene mirar: los de arriba
       * están anchos porque contienen al que se pasa, no por culpa propia. Que
       * el ancestro más cercano venga al lado ahorra el paso de ir subiendo por
       * el inspector.
       */
      const culpable = culpables[culpables.length - 1]

      console.warn(
        `[site] La página se va ${Math.round(sobra)} px de costado con la ventana en ` +
          `${raiz.clientWidth} px.`,
        { culpable, contiene: culpable?.parentElement, todos: culpables },
      )
    }

    /* Se revisa cuando cambia el tamaño de la ventana y cuando cambia el alto del
       contenido, que es la señal de que se dibujó otra pantalla. */
    const observador = new ResizeObserver(revisar)
    observador.observe(document.body)
    return () => observador.disconnect()
  }, [])
}
